// =============================================================================
// FOOTER — Site Footer Component
// =============================================================================
// Clean, balanced footer with:
// 1. Restaurant identity, tagline & official SVG social links
// 2. Direct reservation links (TheFork, OpenTable, etc.)
// 3. Clear opening hours with localized day names
// 4. Contact info with phone & email links
// 5. Quick links & Delivery platforms
// 6. Legal navigation triggers (Privacy Policy, Terms, Accessibility, Cookie Settings)
// 7. Responsive layout tailored for mobile, tablet/iPad, and desktop screens
// =============================================================================

import { useTranslation } from 'react-i18next';
import type { Restaurant, SocialLink, DeveloperConfig } from '@webzite/content-schema';
import {
  PLATFORM_ICON_MAP,
  PhoneIcon,
  MailIcon,
  MapPinIcon,
  ClockIcon,
  ExternalLinkIcon,
} from './icons.js';
import './Footer.css';

export interface ReservationLinkItem {
  label: string;
  url: string;
  provider?: string;
}

export interface GuideLinkItem {
  label: string;
  url: string;
}

export interface FooterProps {
  /** Restaurant data for address, phone, hours, social links */
  restaurant: Restaurant;
  /** Optional custom localized tagline */
  tagline?: string;
  /** The current year for copyright */
  year?: number;
  /** Developer / agency configuration for attribution credit */
  developer?: DeveloperConfig;
  /** Direct reservation links (TheFork, OpenTable, etc.) */
  reservationLinks?: ReservationLinkItem[];
  /** Gastronomic guide & review references (TripAdvisor, Guide Boullenger, etc.) */
  guideLinks?: GuideLinkItem[];
  /** Callback when user clicks a legal link */
  onOpenLegal?: (type: 'privacy' | 'terms' | 'accessibility') => void;
  /** Callback to open cookie settings modal */
  onOpenCookieSettings?: () => void;
  /** Secondary nav links */
  secondaryLinks?: Array<{ label: string; href: string }>;
  /** Delivery buttons component (optional) */
  deliveryButtons?: React.ReactNode;
  /** Newsletter signup component (optional) */
  newsletter?: React.ReactNode;
  /** Translations object for static text */
  labels?: {
    hours?: string;
    contact?: string;
    quickLinks?: string;
    guides?: string;
    reservations?: string;
    privacy?: string;
    terms?: string;
    accessibility?: string;
    cookieSettings?: string;
    allRightsReserved?: string;
    craftedBy?: string;
  };
}

export function Footer({
  restaurant,
  tagline,
  year = new Date().getFullYear(),
  developer,
  reservationLinks = [],
  onOpenLegal,
  onOpenCookieSettings,
  secondaryLinks = [],
  deliveryButtons,
  newsletter,
  guideLinks = [],
  labels = {},
}: FooterProps) {
  const { t } = useTranslation();

  const {
    hours = t('footer.hours', 'Opening Hours'),
    contact = t('footer.contact', 'Contact'),
    quickLinks = t('footer.quicklinks', 'Quick Links'),
    guides = t('footer.guides', 'Guides & Reviews'),
    reservations = t('nav.reserve', 'Reserve a Table'),
    privacy = t('footer.privacy', 'Privacy Policy'),
    terms = t('footer.terms', 'Terms of Service'),
    accessibility = t('footer.accessibility', 'Accessibility'),
    cookieSettings = t('footer.cookies', 'Cookie Settings'),
    allRightsReserved = t('footer.rights', 'All rights reserved.'),
    craftedBy = t('footer.craftedBy', 'Website crafted by'),
  } = labels;

  const displayTagline = tagline ?? t('hero.tagline', { defaultValue: restaurant.tagline });

  return (
    <footer className="site-footer" role="contentinfo">
      {/* Organic sinusoidal wave divider at footer top */}
      <div className="footer-wave-divider" aria-hidden="true">
        <svg
          className="footer-wave-svg"
          viewBox="0 0 1200 24"
          preserveAspectRatio="none"
        >
          <path
            className="footer-wave-path"
            d="M0,24 L1200,24 L1200,16 Q1050,4 900,16 T600,8 T300,16 T0,6 Z"
          />
        </svg>
      </div>

      <div className="container">
        {/* --- Main Content Grid --- */}
        <div className="footer-grid">
          {/* Col 1: Brand, Tagline, Reservations & Social */}
          <div className="footer-col footer-col-brand">
            <h3 className="footer-brand-title">{restaurant.name}</h3>
            {displayTagline && <p className="footer-tagline">{displayTagline}</p>}

            {/* Direct Reservation Links */}
            {reservationLinks.length > 0 && (
              <div className="footer-reservations-wrapper">
                <p className="footer-reservations-label">{reservations}</p>
                <div className="footer-reservation-buttons">
                  {reservationLinks.map((res, idx) => (
                    <a
                      key={idx}
                      href={res.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-reservation-btn"
                    >
                      <span>{res.label}</span>
                      <ExternalLinkIcon width={12} height={12} />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {restaurant.socialLinks.length > 0 && (
              <div className="footer-social-wrapper">
                <SocialIcons links={restaurant.socialLinks} />
              </div>
            )}
          </div>

          {/* Col 2: Opening Hours (Localized days) */}
          <div className="footer-col footer-col-hours">
            <h4 className="footer-heading">
              <span className="footer-heading-icon">
                <ClockIcon width={16} height={16} />
              </span>
              {hours}
            </h4>
            <ul className="footer-hours-list" role="list">
              {restaurant.hours.map((spec, i) => {
                const localizedDays = spec.dayOfWeek
                  .map((d) => t(`days.${d.toLowerCase()}`, { defaultValue: d }))
                  .join(', ');

                return (
                  <li key={i} className="footer-hours-row">
                    <span className="footer-hours-days">{localizedDays}</span>
                    <span className="footer-hours-time">
                      {spec.opens} – {spec.closes}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Col 3: Contact & Address */}
          <div className="footer-col footer-col-contact">
            <h4 className="footer-heading">
              <span className="footer-heading-icon">
                <MapPinIcon width={16} height={16} />
              </span>
              {contact}
            </h4>
            <address className="footer-address">
              <p className="footer-address-line">{restaurant.address.streetAddress}</p>
              <p className="footer-address-line">
                {restaurant.address.postalCode} {restaurant.address.addressLocality}
                {restaurant.address.addressRegion ? `, ${restaurant.address.addressRegion}` : ''}
              </p>

              <div className="footer-contact-links">
                <a href={`tel:${restaurant.phone}`} className="footer-contact-item">
                  <PhoneIcon width={14} height={14} />
                  <span>{restaurant.phone}</span>
                </a>
                <a href={`mailto:${restaurant.email}`} className="footer-contact-item">
                  <MailIcon width={14} height={14} />
                  <span>{restaurant.email}</span>
                </a>
              </div>
            </address>
          </div>

          {/* Col 4: Quick Links & Delivery / Newsletter / Guides */}
          <div className="footer-col footer-col-links">
            {secondaryLinks.length > 0 && (
              <>
                <h4 className="footer-heading">{quickLinks}</h4>
                <ul className="footer-nav" role="list">
                  {secondaryLinks.map((link) => (
                    <li key={link.href}>
                      <a href={link.href} className="footer-nav-link">
                        {link.label}
                      </a>
                    </li>
                  ))}
                  {/* Reservation links also accessible in quick links */}
                  {reservationLinks.map((res, idx) => (
                    <li key={`res-${idx}`}>
                      <a
                        href={res.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-nav-link"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <span>{res.label}</span>
                        <ExternalLinkIcon width={11} height={11} />
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {guideLinks.length > 0 && (
              <div className="footer-guides-slot" style={{ marginTop: 'var(--space-4)' }}>
                <h4 className="footer-heading">{guides}</h4>
                <ul className="footer-nav" role="list">
                  {guideLinks.map((guide, idx) => (
                    <li key={`guide-${idx}`}>
                      <a
                        href={guide.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-nav-link"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <span>{guide.label}</span>
                        <ExternalLinkIcon width={11} height={11} />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {deliveryButtons && <div className="footer-delivery-slot">{deliveryButtons}</div>}

            {newsletter && <div className="footer-newsletter-slot">{newsletter}</div>}
          </div>
        </div>

        {/* --- Bottom Legal Bar --- */}
        <div className="footer-bottom-bar">
          <nav className="footer-legal-nav" aria-label="Legal links">
            <button
              type="button"
              className="footer-legal-btn"
              onClick={() => onOpenLegal?.('privacy')}
            >
              {privacy}
            </button>
            <span className="footer-legal-separator" aria-hidden="true">
              •
            </span>
            <button
              type="button"
              className="footer-legal-btn"
              onClick={() => onOpenLegal?.('terms')}
            >
              {terms}
            </button>
            <span className="footer-legal-separator" aria-hidden="true">
              •
            </span>
            <button
              type="button"
              className="footer-legal-btn"
              onClick={() => onOpenLegal?.('accessibility')}
            >
              {accessibility}
            </button>
            <span className="footer-legal-separator" aria-hidden="true">
              •
            </span>
            <button
              type="button"
              className="footer-legal-btn footer-cookie-trigger"
              onClick={onOpenCookieSettings}
            >
              {cookieSettings}
            </button>
          </nav>

          <div className="footer-bottom-meta">
            <p className="footer-copyright">
              © {year} {restaurant.name}. {allRightsReserved}
            </p>
            {developer?.showFooterCredit !== false && (
              <p className="footer-dev-credit">
                {craftedBy}{' '}
                <a
                  href={developer?.url ?? 'https://portfolio.jeduapf.com'}
                  target="_blank"
                  rel="noopener"
                  className="footer-dev-link"
                >
                  {developer?.name ?? 'jeduapf'}
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}

/** Social media links with official SVG icons */
function SocialIcons({ links }: { links: SocialLink[] }) {
  return (
    <ul className="social-icons" role="list" aria-label="Social media channels">
      {links.map((link) => {
        const IconComponent = PLATFORM_ICON_MAP[link.platform];
        if (!IconComponent) return null;

        return (
          <li key={link.platform}>
            <a
              href={link.url}
              className="social-icon"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Follow us on ${link.platform}`}
            >
              <IconComponent width={18} height={18} />
            </a>
          </li>
        );
      })}
    </ul>
  );
}
